import {
  type QueryFilterExpression,
  type QueryFilterExpressionUnit,
  type QueryFilterExpressionValue,
  type SavedQueryBody,
  type SavedQueryOptions,
} from '@/entities/query/types';

/**
 * Compares two arrays of strings for equality (order-independent)
 */
function areFilterValueArraysEqual(
  a: QueryFilterExpressionValue[],
  b: QueryFilterExpressionValue[],
): boolean {
  if (a.length !== b.length) return false;

  // Sort both arrays and compare
  const sortedA = [...a].sort((a, b) => a.value.localeCompare(b.value));
  const sortedB = [...b].sort((a, b) => a.value.localeCompare(b.value));
  return sortedA.every((value, index) => {
    return value.value === sortedB[index].value && value.type === sortedB[index].type;
  });
}

/**
 * Compares two QueryFilterExpressionUnit objects for equality
 */
function areFilterUnitsEqual(a: QueryFilterExpressionUnit, b?: QueryFilterExpressionUnit): boolean {
  // If b is undefined, return false
  if (!b) return false;
  // Compare logical operator
  if (a.op !== b.op) return false;

  // Compare values arrays
  if (!a.values && !b.values) return true;
  if (!a.values || !b.values) return false;

  return areFilterValueArraysEqual(a.values, b.values);
}

/**
 * Compares two QueryFilterExpression objects for deep equality
 * This is a strict comparison that checks all properties
 */
export function areFiltersEqual(a: QueryFilterExpression, b: QueryFilterExpression): boolean {
  if (a.include.length !== b.include.length) return false;

  return (
    a.include.every((include, index) => areFilterUnitsEqual(include, b.include[index])) &&
    areFilterUnitsEqual(a.exclude, b.exclude)
  );
}

/**
 * Checks if a filter has been modified from its original state
 */
export function hasFilterChanged(
  currentFilter: QueryFilterExpression,
  originalFilter: QueryFilterExpression,
): boolean {
  return !areFiltersEqual(currentFilter, originalFilter);
}

export function hasSavedQueryOptionsChanged(
  currentOptions: SavedQueryOptions,
  originalOptions: SavedQueryOptions,
): boolean {
  return currentOptions.sponsored_post !== originalOptions.sponsored_post;
}

export function hasSavedQueryChanged(
  currentFilter: SavedQueryBody,
  originalFilter: SavedQueryBody,
): boolean {
  return (
    !areFiltersEqual(currentFilter.filter, originalFilter.filter) ||
    hasSavedQueryOptionsChanged(currentFilter.options, originalFilter.options)
  );
}
